import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { BaseView, BaseText } from '@/components/base'
import { AppButton } from '@/components'
import { QRCode, Upload, Verified } from '@/assets/icons'
import { useTheme } from '@/hooks/useTheme'
import { useUserStore } from '@/store'
import { useDeviceShare, useGetMessageAndLinkToShareMyProfile } from '@/hooks'

export const CardProfile: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useTheme()

  // Get user data from store (matching React Native implementation)
  const userAuthenticated = useUserStore((state) => state.userAuthenticated)
  const hasIdentityVerified = userAuthenticated?.hasIdentityVerified

  // Share functionality (matching React Native implementation)
  const { share, copy } = useDeviceShare()
  const { message } = useGetMessageAndLinkToShareMyProfile()

  const handleShareProfile = () => {
    navigate('/share-qr')
  }

  const handleUploadShare = () => {
    share({ message })
  }

  const handleEditProfile = () => {
    navigate('/profile/edit-profile')
  }

  const handleCopyUsername = () => {
    copy(userAuthenticated?.username || '')
  }

  return (
    <BaseView
      px={10}
      py={14}
      rounded={34}
      bgColor="background.cardItemRoot"
      gap={18}
    >
      {/* Header with QR and Share buttons */}
      <BaseView row justify="space-between" align="center">
        <button
          onClick={handleShareProfile}
          className="p-2 rounded-full hover:bg-white/10 transition-colors"
          aria-label="QR Code"
        >
          <QRCode size={32} color={theme.colors.label.default} />
        </button>

        <button
          onClick={handleUploadShare}
          className="p-2 rounded-full transition-colors"
          aria-label="Share"
        >
          <Upload size={24} color={theme.colors.label.default} />
        </button>
      </BaseView>

      {/* Avatar */}
      <BaseView center style={{ marginTop: -38 }}>
        <BaseView circle circleSize={150} center bgColor="background.screen">
          {userAuthenticated?.avatar_path ? (
            <img
              src={userAuthenticated.avatar_path}
              alt="Profile"
              className="w-full h-full rounded-full object-cover"
            />
          ) : (
            <BaseText fontSize={48} fontFamily="bold" color="label.description">
              {userAuthenticated?.fullName?.charAt(0) ||
                userAuthenticated?.first_name?.charAt(0) ||
                userAuthenticated?.username?.charAt(0) ||
                'U'}
            </BaseText>
          )}
        </BaseView>
      </BaseView>

      {/* User Info */}
      <BaseView center gap={6}>
        <BaseView row align="center" gap={6}>
          <BaseText
            fontSize={28}
            fontFamily="semibold"
            color="label.default"
            textAlign="center"
          >
            {userAuthenticated?.fullName || 'User Name'}
          </BaseText>

          {hasIdentityVerified && (
            <Verified
              size={24}
              color={theme.colors.label.verified || '#4EF6EC'}
            />
          )}
        </BaseView>

        <BaseText
          fontSize={18}
          fontFamily="semibold"
          color="label.primary"
          textAlign="center"
          onClick={handleCopyUsername}
        >
          {userAuthenticated?.username
            ? `@${userAuthenticated?.username}`
            : '@username'}
        </BaseText>
      </BaseView>

      {/* Edit Profile Button */}
      <BaseView px={6} pb={4}>
        <AppButton
          style={{
            borderRadius: 20,
          }}
          height={60}
          type="secondaryGray"
          label={t('form:actions.editProfile')}
          onClick={handleEditProfile}
        />
      </BaseView>
    </BaseView>
  )
}
